import React, { useEffect } from "react";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          {/* Header with Z-Store Branding */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-6">
              <img
                src="/zstore_logo.png"
                alt="Z-Store"
                className="h-16 object-contain"
              />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Privacy Policy for Z-Store
            </h1>
            <p className="text-gray-600">Last updated: October 08, 2024</p>
          </div>

          {/* Introduction */}
          <div className="prose max-w-none">
            <p className="text-gray-600 mb-8">
              This Privacy Policy describes Z-Store's policies and procedures on
              the collection, use and disclosure of Your information when You
              use the Service and tells You about Your privacy rights and how
              the law protects You.
            </p>
            <p className="text-gray-600 mb-8">
              We use Your Personal data to provide and improve the Z-Store
              Service. By using the Service, You agree to the collection and use
              of information in accordance with this Privacy Policy.
            </p>

            {/* Interpretation and Definitions */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Interpretation and Definitions
              </h2>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Interpretation
              </h3>
              <p className="text-gray-600 mb-6">
                The words of which the initial letter is capitalized have
                meanings defined under the following conditions. The following
                definitions shall have the same meaning regardless of whether
                they appear in singular or in plural.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Definitions
              </h3>
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="space-y-4">
                  {[
                    {
                      term: "Account",
                      definition:
                        "A unique account created for You to access our Service or parts of our Service.",
                    },
                    {
                      term: "Affiliate",
                      definition:
                        'An entity that controls, is controlled by or is under common control with a party, where "control" means ownership of 50% or more of the shares, equity interest or other securities entitled to vote for election of directors or other managing authority.',
                    },
                    {
                      term: "Application",
                      definition:
                        "Z-Store, the software program provided by the Company.",
                    },
                    {
                      term: "Company",
                      definition:
                        "Z-Store, operated by ZagSystems, located at Ghasham, Zagazig, Egypt.",
                    },
                    { term: "Country", definition: "Egypt" },
                    {
                      term: "Device",
                      definition:
                        "Any device that can access the Service such as a computer, a cellphone or a digital tablet.",
                    },
                    {
                      term: "Personal Data",
                      definition:
                        "Any information that relates to an identified or identifiable individual.",
                    },
                    {
                      term: "Service",
                      definition: "The Z-Store Application and Website.",
                    },
                    {
                      term: "Service Provider",
                      definition:
                        "Any natural or legal person who processes the data on behalf of the Company. It refers to third-party companies or individuals employed by the Company to facilitate the Service, to provide the Service on behalf of the Company, to perform services related to the Service or to assist the Company in analyzing how the Service is used.",
                    },
                    {
                      term: "Usage Data",
                      definition:
                        "Data collected automatically, either generated by the use of the Service or from the Service infrastructure itself (for example, the duration of a page visit).",
                    },
                    {
                      term: "You",
                      definition:
                        "The individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.",
                    },
                  ].map(({ term, definition }, index) => (
                    <li key={index} className="flex flex-col md:flex-row">
                      <span className="font-semibold text-gray-900 md:w-1/4">
                        {term}:
                      </span>
                      <span className="text-gray-600 md:w-3/4">
                        {definition}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* Types of Data Collected */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Collecting and Using Your Personal Data
              </h2>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Types of Data Collected
              </h3>

              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">
                    Personal Data
                  </h4>
                  <p className="text-gray-600 mb-4">
                    While using Our Service, We may ask You to provide Us with
                    certain personally identifiable information that can be used
                    to contact or identify You. Personally identifiable
                    information may include, but is not limited to:
                  </p>
                  <ul className="list-disc list-inside text-gray-600 ml-4 space-y-2">
                    <li>Email address</li>
                    <li>First name and last name</li>
                    <li>Phone number</li>
                    <li>Address, State, Province, ZIP/Postal code, City</li>
                    <li>Usage Data</li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">
                    Usage Data
                  </h4>
                  <p className="text-gray-600 mb-4">
                    Usage Data is collected automatically when using the
                    Service.
                  </p>
                  <p className="text-gray-600">
                    Usage Data may include information such as Your Device's
                    Internet Protocol address (e.g. IP address), browser type,
                    browser version, the pages of our Service that You visit,
                    the time and date of Your visit, the time spent on those
                    pages, unique device identifiers and other diagnostic data.
                  </p>
                </div>
              </div>
            </section>

            {/* Use of Personal Data */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Use of Your Personal Data
              </h2>
              <p className="text-gray-600 mb-4">
                The Company may use Personal Data for the following purposes:
              </p>

              <div className="bg-gray-50 rounded-xl p-6">
                <ul className="space-y-4">
                  {[
                    "To provide and maintain our Service, including to monitor the usage of our Service.",
                    "To manage Your Account: to manage Your registration as a user of the Service.",
                    "For the performance of a contract: the development, compliance and undertaking of the purchase contract for the products, items or services You have purchased or of any other contract with Us through the Service.",
                    "To contact You: To contact You by email, telephone calls, SMS, or other equivalent forms of electronic communication.",
                    "To provide You with news, special offers and general information about other goods, services and events.",
                    "To manage Your requests: To attend and manage Your requests to Us.",
                    "For business transfers: We may use Your information to evaluate or conduct a merger, divestiture, restructuring, reorganization, dissolution, or other sale or transfer of some or all of Our assets.",
                    "For other purposes: Such as data analysis, identifying usage trends, determining the effectiveness of our promotional campaigns.",
                  ].map((purpose, index) => (
                    <li key={index} className="flex items-start">
                      <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-800 text-sm font-medium mr-3 flex-shrink-0">
                        {index + 1}
                      </span>
                      <span className="text-gray-600">{purpose}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* Security Section */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Security of Your Personal Data
              </h2>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-xl">
                <p className="text-gray-700">
                  The security of Your Personal Data is important to Us, but
                  remember that no method of transmission over the Internet, or
                  method of electronic storage is 100% secure. While We strive
                  to use commercially acceptable means to protect Your Personal
                  Data, We cannot guarantee its absolute security.
                </p>
              </div>
            </section>

            {/* Contact Section */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Contact Us
              </h2>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-gray-600 mb-4">
                  If you have any questions about this Privacy Policy, You can
                  contact Z-Store:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center text-gray-600">
                    <svg
                      className="h-6 w-6 mr-3 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    By email:{" "}
                    <a
                      href="mailto:sales@zagSystems.org"
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      sales@zagSystems.org
                    </a>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <svg
                      className="h-6 w-6 mr-3 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                      />
                    </svg>
                    Website:{" "}
                    <a
                      href="https://zagsystems.org"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      zagsystems.org
                    </a>
                  </li>
                </ul>
              </div>
            </section>

            {/* Footer Note */}
            <div className="text-center text-sm text-gray-500 mt-12">
              <p>© 2024 Z-Store. All rights reserved.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
